// Studios & projects: every studio with its footprint; the open studio in the right panel.
import React from 'react';
import type { StudioDetail, StudioRow } from '../../../shared/types';
import { GAsyncState, GCard, GEyebrow, GH2, GPill, GStatus, inputStyle } from '../design/atoms';
import { DbPending, isDbUnavailable } from '../design/DbPending';
import { DetailHeader, DetailSection, MasterDetail, ROW_CAP, RowCapNote, selectedRow, useEscape } from '../design/MasterDetail';
import { useActGate } from '../design/useAct';
import { fmtBytes, fmtDateTime } from '../fmt';
import { useResource } from '../store/resourceStore';
import { useSelection } from '../store/uiStore';

const none = <div style={{ fontFamily: 'var(--ff-code)', fontSize: 12.5, color: 'var(--fg-4)' }}>None.</div>;

function Detail({ id, onClose }: { id: string; onClose: () => void }) {
  const r = useResource<StudioDetail>(`/api/studios/${id}`);
  const gate = useActGate();
  const d = r.data;
  return (
    <>
      <DetailHeader title={d?.studio.label ?? id} eyebrow={d ? `${d.studio.studioName} · ${id}` : id} onClose={onClose} />
      <GAsyncState busy={r.loading && !r.data} error={r.error} onRetry={r.reload} />
      {d && (
        <>
          <div style={{ fontFamily: 'var(--ff-code)', fontSize: 12.5 }}><span style={{ color: 'var(--fg-4)' }}>owner </span>{d.studio.ownerEmail ?? d.studio.ownerId ?? 'none'}{!d.studio.ownerIsMember && <span style={{ color: 'var(--s-review-fg)' }}> · not a member</span>}</div>
          <DetailSection label={`Roster · ${d.roster.length}`}>
            <table className="pd">
              <thead><tr><th>Member</th><th>Studio role</th><th>Project roles</th></tr></thead>
              <tbody>{d.roster.map((m) => (
                <tr key={m.studioUserId}><td>{m.email ?? <span style={{ color: 'var(--fg-4)' }}>no email</span>}{m.isOwner && <span style={{ fontFamily: 'var(--ff-code)', fontSize: 11, color: 'var(--accent)', marginLeft: 8 }}>owner</span>}</td><td style={{ fontFamily: 'var(--ff-code)', fontSize: 12 }}>{m.studioRoles.join(', ') || '–'}</td><td style={{ fontFamily: 'var(--ff-code)', fontSize: 12 }}>{m.projectRoles.join(', ') || '–'}</td></tr>
              ))}</tbody>
            </table>
          </DetailSection>
          <DetailSection label={`Projects · ${d.projects.length}`}>
            {d.projects.length === 0 ? none : (
              <table className="pd"><thead><tr><th>Project</th><th>Name</th><th className="num">Grants</th></tr></thead>
                <tbody>{d.projects.map((p) => <tr key={p.id} title={p.id}><td>{p.label}</td><td style={{ fontFamily: 'var(--ff-code)', fontSize: 12 }}>{p.name}</td><td className="num">{p.grants}</td></tr>)}</tbody></table>
            )}
          </DetailSection>
          <DetailSection label={`Machines · ${d.machines.length}`}>
            {d.machines.length === 0 ? none : (
              <table className="pd"><thead><tr><th>Machine</th><th className="num">Roles</th></tr></thead>
                <tbody>{d.machines.map((m) => <tr key={m.id} title={m.id}><td>{m.label}</td><td className="num">{m.roles}</td></tr>)}</tbody></table>
            )}
          </DetailSection>
          <DetailSection label={`Invites · ${d.invites.length}`}>
            {d.invites.length === 0 ? none : (
              <table className="pd"><thead><tr><th>Email</th><th>State</th><th>Expires</th></tr></thead>
                <tbody>{d.invites.map((i) => <tr key={i.id}><td>{i.targetEmail}</td><td><GStatus kind={i.pending ? 'work' : 'dim'} label={i.pending ? 'pending' : 'expired'} />{i.hasClerkInvitation ? '' : <span style={{ color: 'var(--fg-4)', fontFamily: 'var(--ff-code)', fontSize: 11 }}> no Clerk invitation</span>}</td><td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(i.expiration)}</td></tr>)}</tbody></table>
            )}
          </DetailSection>
          <DetailSection label={`Studio config · ${d.config.length} keys`}>
            {d.config.length === 0 ? none : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontFamily: 'var(--ff-code)', fontSize: 12 }}>
                {d.config.map((c) => <div key={c.key} style={{ display: 'flex', gap: 10, minWidth: 0 }}><span style={{ color: 'var(--fg-2)', flexShrink: 0 }}>{c.key}</span><span style={{ color: 'var(--fg-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.value}>{c.value}</span></div>)}
              </div>
            )}
          </DetailSection>
          {!gate.allowed && <div style={{ fontFamily: 'var(--ff-code)', fontSize: 11.5, color: 'var(--fg-4)', marginTop: 14 }}>{gate.why}</div>}
        </>
      )}
    </>
  );
}

export function Studios() {
  const r = useResource<StudioRow[]>('/api/studios');
  const [q, setQ] = React.useState('');
  const [open, setOpen] = useSelection('studios');
  const close = React.useCallback(() => setOpen(null), [setOpen]);
  useEscape(close, !!open);
  const filtered = (r.data ?? []).filter((s) => !q || [s.label, s.studioName, s.ownerEmail, s.id].some((v) => v?.toLowerCase().includes(q.toLowerCase())));
  const rows = filtered.slice(0, ROW_CAP);
  const leaks = (r.data ?? []).filter((s) => s.members === 0 || !s.ownerIsMember);
  const list = isDbUnavailable(r.error) ? <DbPending error={r.error!} onRetry={r.reload} /> : (
    <GCard padding="18px 20px">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <GH2 size={17}>Studios</GH2>
        <GEyebrow style={{ fontSize: 12 }}>{r.data ? `${r.data.length} studios${leaks.length ? ` · ${leaks.length} with no members or an owner who is not a member` : ''}` : ''}</GEyebrow>
        <div style={{ flex: 1 }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search label, name, owner or id" style={{ ...inputStyle, width: 300, padding: '7px 12px' }} />
        <GPill onClick={r.reload} disabled={r.loading}>Refresh</GPill>
      </div>
      <GAsyncState busy={r.loading && !r.data} error={r.error} onRetry={r.reload} />
      {r.data && (
        <>
          <table className="pd">
            <thead><tr><th>Studio</th><th>Owner</th><th className="num">Members</th><th className="num">Projects</th><th className="num">Machines</th><th className="num">Invites</th><th className="num">Packages</th><th>Check</th></tr></thead>
            <tbody>{rows.map((s) => (
              <tr key={s.id} className="row-hover" aria-selected={open === s.id || undefined} style={selectedRow(open === s.id)} onClick={() => setOpen(s.id)}>
                <td>{s.label} <span style={{ color: 'var(--fg-4)', fontFamily: 'var(--ff-code)', fontSize: 12 }}>{s.studioName}</span></td>
                <td>{s.ownerEmail ?? <span style={{ color: 'var(--fg-4)' }}>{s.ownerId ? 'unknown user' : 'none'}</span>}</td>
                <td className="num">{s.members}</td><td className="num">{s.projects}</td><td className="num">{s.machines}</td>
                <td className="num">{s.invitesPending}{s.invitesExpired ? <span style={{ color: 'var(--fg-4)' }}> +{s.invitesExpired} expired</span> : null}</td>
                <td className="num">{s.packages}{s.packageBytes ? <span style={{ color: 'var(--fg-4)' }}> · {fmtBytes(s.packageBytes)}</span> : null}</td>
                <td>{s.members === 0 ? <GStatus kind="warn" label="no members" /> : !s.ownerIsMember ? <GStatus kind="warn" label="owner not a member" /> : <GStatus kind="ok" label="ok" />}</td>
              </tr>
            ))}</tbody>
          </table>
          <RowCapNote shown={rows.length} total={filtered.length} />
        </>
      )}
    </GCard>
  );
  return <MasterDetail list={list} detail={open ? <Detail key={open} id={open} onClose={close} /> : null} />;
}
